import * as Tone from 'tone';

export class MidiAudioHandler {
  private samplerRef: React.MutableRefObject<Tone.Sampler | null>;
  private samplerLoudRef: React.MutableRefObject<Tone.Sampler | null>;
  private shouldPlaySoundRef: React.MutableRefObject<boolean>;
  private sustainRef: React.MutableRefObject<boolean>;
  private isScrubbingRef: React.MutableRefObject<boolean>;
  private activeNotesRef: React.MutableRefObject<number[]>;
  private heldNotesRef: React.MutableRefObject<number[]>;
  private toggledNotesRef: React.MutableRefObject<number[]>;
  private soundingNotes = new Set<number>();
  private heldNotes = new Set<number>();
  private audioContext: AudioContext;
  private readonly VELOCITY_THRESHOLD = 100; // Velocities >= 100 use loud layer
  private selectedInstrumentRef: React.MutableRefObject<string>;

  // Tone.Sampler has no polyphony cap: every triggerAttack starts another copy
  // of the sample, and identical copies started at the same instant sum in
  // phase, so a handful of duplicates is enough to clip the output. Scrubbing
  // across a span can ask for the same note many times in one audio moment, so
  // ignore a restrike that lands too soon after the previous one to be heard as
  // a separate attack. The fastest recorded human repetition of a single note
  // is ~60ms, so 40ms never suppresses a real performance at 1x speed.
  private lastAttackTimes = new Map<number, number>();
  private readonly MIN_RETRIGGER_MS = 40;

  // The keys down right now, by this handler's own count of note-ons and
  // note-offs. The pedal-up release reads it rather than React's note lists:
  // those are copied into refs after a render, and a busy main thread lets
  // the player's batches run ahead of renders, so a key struck again just
  // before the pedal lifts can still be listed as held, and be cut off.
  private keysDown = new Set<number>();
  // The notes this handler left to the pedal: their note-off came while it
  // was down. Pedal-up releases these by its own count too, since a key
  // lifted just before the pedal is not yet in heldNotesRef, and is still in
  // activeNotesRef, until App renders.
  private pedalHeld = new Set<number>();
  trackKey(data: number[]) {
    const status = data[0] & 0xF0;
    if (status === 144 && data[2] > 0) this.keysDown.add(data[1]);
    else if (status === 128 || status === 144) this.keysDown.delete(data[1]);
  }

  constructor(
    samplerRef: React.MutableRefObject<Tone.Sampler | null>,
    samplerLoudRef: React.MutableRefObject<Tone.Sampler | null>,
    shouldPlaySoundRef: React.MutableRefObject<boolean>,
    sustainRef: React.MutableRefObject<boolean>,
    isScrubbingRef: React.MutableRefObject<boolean>,
    activeNotesRef: React.MutableRefObject<number[]>,
    heldNotesRef: React.MutableRefObject<number[]>,
    selectedInstrumentRef: React.MutableRefObject<string>,
    toggledNotesRef: React.MutableRefObject<number[]>
  ) {
    this.samplerRef = samplerRef;
    this.samplerLoudRef = samplerLoudRef;
    this.shouldPlaySoundRef = shouldPlaySoundRef;
    this.sustainRef = sustainRef;
    this.isScrubbingRef = isScrubbingRef;
    this.activeNotesRef = activeNotesRef;
    this.heldNotesRef = heldNotesRef;
    this.selectedInstrumentRef = selectedInstrumentRef;
    this.toggledNotesRef = toggledNotesRef;
    this.audioContext = Tone.getContext().rawContext as AudioContext;
  }

  // Calculate release time based on MIDI note number and velocity (for piano instruments only)
  private getReleaseTime(midiNote: number, velocity: number): number {
    const instrument = this.selectedInstrumentRef.current;
    const isPiano = instrument === 'PianoGrand' || instrument === 'PianoFelt' || instrument === 'PianoUpright';
    const BASE_RELEASE = 0.5;

    if (!isPiano) return BASE_RELEASE;

    // Piano-specific release times (simulates damper behavior)
    if (midiNote <= 49) {
      // Low register: both lower notes and higher velocities = longer release
      const NOTE_RANGE = 28;        // 49 - 21
      const VEL_RANGE = 127;        // 128 - 1
      const RELEASE_SPAN = 0.9;     // 1.5 - 0.6

      const notePct = (midiNote - 21) / NOTE_RANGE;
      const velPct = (velocity - 1) / VEL_RANGE;
      return Number((BASE_RELEASE + velPct * (1.0 - RELEASE_SPAN * notePct)).toFixed(2));
    }

    // Above G6 - no damper felts on highest notes, ring freely for 90 seconds due to exponential decay (not linear)
    // Except while scrubbing: a fast drag can spray dozens of top-octave notes,
    // and 90-second tails would pile up voice on voice with nothing to end them.
    if (midiNote > 91) {
      if (this.isScrubbingRef.current) return BASE_RELEASE;
      return 90;
    }
    return BASE_RELEASE;
  }

  // Everything has been released by someone else (Clear All Notes releases the
  // samplers directly), so forget what was sounding and drop the retrigger
  // cooldowns: a note that has just been released can be attacked again
  // immediately, and without this the next attack would be read as a restrike
  // and skipped.
  // stillSounding: notes deliberately left ringing, so they stay on the books.
  clearNoteTracking(stillSounding: number[] = []) {
    this.soundingNotes.clear();
    this.lastAttackTimes.clear();
    this.keysDown.clear();
    this.pedalHeld.clear();
    stillSounding.forEach(note => this.soundingNotes.add(note));
  }

  handleMidiForAudio(data: number[], isPrecomputed: boolean = false, isNoteOn: boolean = false) {
    this.trackKey(data);
    if (!this.shouldPlaySoundRef.current || !this.samplerRef.current || !this.samplerRef.current.loaded) return;

    const status = data[0] & 0xF0;

    if (status === 144 && data[2] > 0) { // Note on
      // Skip restrikes too close together to be heard as separate attacks
      // (see MIN_RETRIGGER_MS) - they only stack voices and add volume
      const nowMs = performance.now();
      const lastAttackMs = this.lastAttackTimes.get(data[1]);
      if (lastAttackMs !== undefined && nowMs - lastAttackMs < this.MIN_RETRIGGER_MS) return;
      this.lastAttackTimes.set(data[1], nowMs);

      const velocityNormd = Math.min(Math.max((data[2] + 1) / (127 + 20), 0), 1);
      const noteName = Tone.Midi(data[1]).toNote();
      const when = this.audioContext.currentTime;

      // Select sampler based on velocity
      const useLoudLayer = data[2] >= this.VELOCITY_THRESHOLD &&
        this.samplerLoudRef.current &&
        this.samplerLoudRef.current.loaded;
      const activeSampler = useLoudLayer ? this.samplerLoudRef.current : this.samplerRef.current;

      // Set release time based on note number and velocity (for pianos) before triggering attack
      const releaseTime = this.getReleaseTime(data[1], data[2]);
      this.samplerRef.current.release = releaseTime;
      if (this.samplerLoudRef.current && this.samplerLoudRef.current.loaded) {
        this.samplerLoudRef.current.release = releaseTime;
      }

      try { // release sample before retriggering
        // For precomputed data: only release if this is a note-on event and note is already sounding (retriggering)
        // For regular data: release if already active and sustain is on

        // The below causes pops and lowered volume
        // if (this.soundingNotes.has(data[1]) &&
        //   ((isPrecomputed && isNoteOn) || (!isPrecomputed && this.sustainRef.current && this.heldNotesRef.current.includes(data[1])))) {
        //   this.samplerRef.current.triggerRelease(noteName, 0);
        // }

        activeSampler!.triggerAttack(noteName, when, velocityNormd);
        this.soundingNotes.add(data[1]);
      } catch (error) { // console.debug('Sampler not ready:', error);
        return;
      }

    } else if (status === 128 || (status === 144 && data[2] === 0)) { // Note off

      try {
        // Release unless a pedal is holding the note. Dragging the scrub bar
        // always releases, so a fast drag can't pile notes up.
        //
        // Precomputed note-offs (scrubbing and the navigation buttons) used to
        // bypass this test entirely, on the grounds that the file's own sustain
        // is already baked into the precomputed data - true, but it also made
        // the LIVE pedal impossible to honour while navigating. It no longer
        // needs to: those paths force file sustain off and push the merged
        // state through onSustainStateChange, so during navigation sustainRef
        // means the live pedal and nothing else. Holding the pedal while
        // stepping through a file now accumulates the notes, which is the point
        // (hear what a melodic run adds up to).
        if (this.isScrubbingRef.current || !this.sustainRef.current) {
          const noteName = Tone.Midi(data[1]).toNote();
          const when = this.audioContext.currentTime;
          // Release from both samplers (we don't track which one was used)
          this.samplerRef.current.triggerRelease(noteName, when);
          if (this.samplerLoudRef.current && this.samplerLoudRef.current.loaded) {
            this.samplerLoudRef.current.triggerRelease(noteName, when);
          }
          this.soundingNotes.delete(data[1]);
          this.pedalHeld.delete(data[1]);
        } else {
          this.pedalHeld.add(data[1]);
        }
        // Note: heldNotes management stays in React for state consistency
      } catch (error) { // console.debug('Sampler not ready:', error);
        return;
      }

    } else if (data[0] === 176 && data[1] === 64) { // Sustain pedal
      if (data[2] < 64) { // Pedal up - release held notes
        try {
          // Get notes to release: held, but not still sounding for another
          // reason. A key can be down again (activeNotes), or the note can be
          // at the file's current position while paused (toggledNotes) - the
          // playhead can return to a pitch the pedal is already holding, and
          // releasing it here would cut a note that is legitimately sounding.
          // A note this handler left to the pedal is judged by its own count
          // of keys down alone (see pedalHeld).
          const fromPedal = Array.from(this.pedalHeld)
            .filter(midiNote => !this.keysDown.has(midiNote));
          const fromApp = this.heldNotesRef.current
            .filter(midiNote => !this.pedalHeld.has(midiNote))
            .filter(midiNote => !this.keysDown.has(midiNote))
            .filter(midiNote => !this.activeNotesRef.current.includes(midiNote));
          const notesToRelease = fromPedal.concat(fromApp)
            .filter(midiNote => !this.toggledNotesRef.current.includes(midiNote));
          this.pedalHeld.clear();
          const noteNames = notesToRelease.map(midiNote => Tone.Midi(midiNote).toNote());
          const when = this.audioContext.currentTime;
          // Release from both samplers
          this.samplerRef.current.triggerRelease(noteNames, when);
          if (this.samplerLoudRef.current && this.samplerLoudRef.current.loaded) {
            this.samplerLoudRef.current.triggerRelease(noteNames, when);
          }

          // Clean up soundingNotes for released notes
          notesToRelease.forEach(note => this.soundingNotes.delete(note));
        } catch (error) { // console.debug('Sampler not ready:', error);
          return;
        }
      }
    }
  }
}